import TableZoneList from "@/samples/jobchain/tables.json";
import { defineStore } from "pinia";
import _ from "lodash";
import dayjs from "dayjs";
import PreviewBuilder from "~/class/PreviewBuilder";
import type { Table } from "~/interface/Table";
import * as TableHelper from "~/class/TableHelper";
import ReceiptBuilder from "~/class/ReceiptBuilder";
import Swal from "sweetalert2";
import JobBuilder from "~/class/JobBuilderV1";
import { JobAction, JobMode } from "~/interface/Job";
import { calculateBilling } from "~/class/TableHelper";
import { usePosJobsStore } from "./pos.jobs";
import type { ReceiptOrderDynamodb } from "~/interface/ReceiptOrder";
import { ReceiptOrderClass } from "~/class/ReceiptOrder";


export const usePosTableInstanceStore = defineStore("table-instance", () => {



  //STATES
  const selectedTableId = ref("A1")
  const itemsSelected = ref<Record<string, any>>({});
  const uuidQtyToVoid = ref<Record<string, number>>({});
  const billFilter = ref('all');
  const forceShowSplitBillColumn = ref(false)
  watch(selectedTableId, () => {
    itemsSelected.value = {}
    uuidQtyToVoid.value = {}
    billFilter.value = 'all'
    forceShowSplitBillColumn.value = false
  })

  const selectItem = (item: any) => {
    if (itemsSelected.value[item.uuid]) {
      delete itemsSelected.value[item.uuid]
    } else {
      itemsSelected.value[item.uuid] = item
    }
  }

  const selectOnlyItem = (item: any) => {
    itemsSelected.value = {
      [item.uuid]: item
    }
  }

  const selectAllItems = () => {
    const allSelected = data.value.items?.every(item => itemsSelected.value[item.uuid])
    if (allSelected) {
      itemsSelected.value = {}
    } else {
      for (const item of data.value.items || []) {
        itemsSelected.value[item.uuid] = item
      }
    }
  }
  const clearItemsSelected = () => {
    itemsSelected.value = {}
  }
  const clearTableSelected = () => {
    selectedTableId.value = ""
  }


  const getCountItemSelected = computed(() => Object.keys(itemsSelected.value).length)

  //GETTERS
  const data = computed(() => {

    const jobsStore = usePosJobsStore()
    const instance = jobsStore.tables[selectedTableId.value] || {
      items: [],
      __items: [],
      mode: 1,  //todo, check mode later
      name: selectedTableId.value
    }
    return instance
  })

  const billList = computed(() => {
    const bills = data.value.items?.map(item => item.bill)

    return _.uniq(bills).sort((a, b) => a - b)
  })

  // The split-bill number to stamp on a printed slip's title (→ "A5-2"), or null
  // when no suffix should show. Only when the table genuinely has >1 bill AND a
  // specific bill (not "All") is selected. Single-bill tables and the "All" view
  // print the plain table name. Used by both Payment (pos.payment) and Print
  // Preview (printPreview below); the print builder reads receipt.SplitBillNo.
  const selectedSplitBillNo = computed<number | null>(() => {
    if (billList.value.length > 1 && billFilter.value !== 'all') {
      return Number(billFilter.value)
    }
    return null
  })

  const itemsDisplay = computed(() => {

    if (billFilter.value == 'all') {
      return data.value.items;
    } else {
      const items_Filtted = data.value.items?.filter(item => item.bill == billFilter.value)
      return items_Filtted
    }
  })

  // Keep the selection a subset of what is on screen. A selected line can leave
  // the view without the cashier touching it — voided from a phone / another
  // cashier, or hidden by the split-bill tab. A stale uuid made the action modals
  // count it (and enable submit) while selectFromUuids dropped it, so a discount
  // was written with json2 = {} (RTDB drops it → replay crash, Sentry 7746015332).
  watch(itemsDisplay, (list) => {
    const alive = new Set((list || []).map((item: any) => item.uuid))
    const dead = Object.keys(itemsSelected.value).filter((uuid) => !alive.has(uuid))
    if (dead.length === 0) return
    dead.forEach((uuid) => delete itemsSelected.value[uuid])
  })

  const summaryBillingMoney = computed(() => {
    const { tableBillingGlobal, tablesState } = usePosTableStatesStore()
    const settingsStore = useSettingsStore()
    const noServiceCharge = data.value?.mode === JobMode.TOGO && settingsStore.togoNoService
    return calculateBilling(itemsDisplay.value, tableBillingGlobal, tablesState[selectedTableId.value], { noServiceCharge })
  })

  const table = computed(() => {
    const { tablesState } = usePosTableStatesStore()
    const tableState = tablesState[selectedTableId.value] || {}

    return {
      ...data.value,
      guests: tableState.guests ?? data.value?.guests ?? 0,
      setting: tableState
    }
  })

  const filteredBilling = computed(() => summaryBillingMoney.value)
  const isLock = computed(() => {
    const tableStateStore = usePosTableStatesStore()
    return tableStateStore.isLockedWithTable(selectedTableId.value)
  })
  const itemCount = computed(() => itemsDisplay.value?.length || 0)



  //ACTIONS
  const printPreview = () => {
    const tableInstanceStore = usePosTableInstanceStore()
    const employeeStore = usePosEmployeesStore();
    const shopStore = usePosShopStore();
    const _customerId = shopStore.getStoreId
    const _shiftWork = usePosJobsStore().getShift()
    const _user = employeeStore.getCurrentEmployee?.name || 'admin'

    const tableCloned: Table = _.cloneDeep(tableInstanceStore.data);
    tableCloned.items = tableInstanceStore.itemsDisplay
    tableCloned.billing = tableInstanceStore.summaryBillingMoney
    tableCloned.endDateTime = dayjs().format("YYYY-MM-DDTHH:mm:ss");
    // Guest count lives in tableStates (via the merged `table` getter), not on
    // `data` — carry it onto the clone so the preview shows Customer count too.
    tableCloned.guests = table.value?.guests ?? tableCloned.guests ?? 0;

    const receiptOrderDynamodb: ReceiptOrderDynamodb = ReceiptOrderClass
      .fromTable(tableCloned, {
        orderId: "",
        shiftWork: _shiftWork,
        user: _user,
        payment: { list: [] },
        customerId: _customerId,
        printMode: 'preview'
      })
      .toDynamodb();

    // Split-bill title suffix (→ "Table A5-2"). Extra field only; Table stays "A5"
    // so reports/grouping are unaffected. null on the "All" view or single-bill.
    ;(receiptOrderDynamodb as any).SplitBillNo = selectedSplitBillNo.value

    //for print preview
    return receiptOrderDynamodb
  }


  const addRequest = () => {

    // JOB is a one-shot order — block re-adding items to an existing JOB.
    if (data.value?.mode === JobMode.JOB) {
      return
    }

    if (!selectedTableId.value) {
      Swal.fire({
        icon: "warning",
        title: "กรุณาเลือกโต๊ะ",
        text: "ไม่ได้เลือกโต๊ะ กรุณาเลือกโต๊ะก่อนเพิ่มเมนู",
        confirmButtonText: "OK",
      })
      return
    }



    const jobsStore = usePosJobsStore()
    if (jobsStore.isRunningOverClosedDay()) {
      jobsStore.showRunningOverClosedDayAlert()
      return
    }

    const cartStore = usePosCartStore()

    // table instance data is passsed to cart store
    cartStore.addTableRequest(data.value)

    const tabTypeByMode: Record<number, string> = {
      [JobMode.TOGO]: 'togo',
      [JobMode.JOB]: 'job',
      [JobMode.DELIVERY]: 'delivery',
      [JobMode.TABLE]: 'table',
    }
    useTabs().setTab('cart', (tabTypeByMode[data.value?.mode] || 'table') as any)

  }

  const setTable = (targetTable: any) => {
    selectedTableId.value = targetTable?.name || targetTable?.uuid || ""
  }

  const setGuests = (guests: number) => {
    if (!selectedTableId.value) {
      return
    }

    const tableStateStore = usePosTableStatesStore()
    tableStateStore.setTableBillingSetting(selectedTableId.value, {
      guests
    })
  }

  const move = async (targetTableId: string) => {
    const sourceTableId = selectedTableId.value
    const json2 = {}

    let isMoveAll = false
    // move all items
    if (Object.keys(itemsSelected.value).length === 0 || Object.keys(itemsSelected.value).length === data.value.items?.length) {
      data.value.items?.forEach((item: any) => {
        json2[item.uuid] = true
      })
      isMoveAll = true
    }

    // move selected items
    else {
      Object.keys(itemsSelected.value).forEach((uuid: string) => {
        json2[uuid] = true
      })
    }

    // Moving every item empties the source table — carry the same "table is now
    // closed" flag as VOID/PAY so downstream sees a consistent property.
    const isTableClosed = isMoveAll

    const jobCard = saveToJobchain(JobAction.MOVE_TO, json2, {
      newAlias: targetTableId,
      newUuid: targetTableId,
      mode: 1,
      isMoveAll: isMoveAll,
      isTableClosed: isTableClosed
    })
    //delay to add another job move From
    await new Promise(resolve => setTimeout(resolve, 10))

    saveToJobchain(JobAction.MOVE_RECEIVED, json2, {
      alias: targetTableId,
      uuid: targetTableId,
      mode: 1,
      isMoveAll: isMoveAll,
      isTableClosed: isTableClosed,
      oldAlias: sourceTableId,
      __items: jobCard.__items
    })

    // Move-all empties the source and pours every item into the destination, so
    // the source's per-table settings (service charge / discount / VAT / timer)
    // should follow the party to the destination — then vanish from the source.
    //   - destination is a fresh table       → it inherits the source's settings.
    //   - destination already has settings    → keep ITS values (the destination
    //     is authoritative) and only fill in keys it doesn't have yet.
    // `lock` is an edit guard, not a party setting — never carry it over.
    // All writes hit RTDB directly, so they sync to every terminal on their own.
    if (isTableClosed) {
      const stateStore = usePosTableStatesStore()
      const sourceState = stateStore.tablesState[sourceTableId] || {}
      const destState = stateStore.tablesState[targetTableId] || {}

      const carryOver: Record<string, any> = {}
      for (const key of Object.keys(sourceState)) {
        if (key === 'lock') continue
        const destHasValue = destState[key] !== undefined && destState[key] !== null
        if (!destHasValue) {
          carryOver[key] = sourceState[key]
        }
      }

      if (Object.keys(carryOver).length > 0) {
        stateStore.setTableBillingSetting(targetTableId, carryOver)
      }
      stateStore.clearTableState(sourceTableId)
    }

  }

  const isVoiding = ref(false)
  const voidItems = (reason: string, opts: { printToKitchen?: boolean; copyToCashier?: boolean } = {}) => {
    if (isVoiding.value) return
    isVoiding.value = true
    try {
      const sourceTableId = selectedTableId.value
      const json2 = Object.values(itemsSelected.value).reduce((acc: any, item: any) => {
        acc[item.uuid] = (uuidQtyToVoid.value[item.uuid] || 0) * -1
        return acc
      }, {})

      // Voiding the full quantity of every item on the table empties it. Check
      // against `data.value.items` (all items) — not `itemsDisplay`, which may be
      // bill-filtered — so a partial-bill void doesn't wrongly mark the table closed.
      const allItems = data.value.items || []
      const isTableClosed = allItems.length > 0 && allItems.every((item: any) =>
        itemsSelected.value[item.uuid] && (uuidQtyToVoid.value[item.uuid] || 0) >= item.quantity
      )

      saveToJobchain(JobAction.VOID, json2, {
        __reason: reason || "",
        isTableClosed: isTableClosed,
        ...(opts.printToKitchen === false ? { __noPrint: true } : {}),
        ...(opts.copyToCashier === true ? { __copyToCashier: true } : {}),
      })

      // Table is empty now — drop its leftover billing state. Writes RTDB
      // directly, so it syncs to every terminal on its own.
      if (isTableClosed) {
        usePosTableStatesStore().clearTableState(sourceTableId)
      }
    } finally {
      isVoiding.value = false
    }
  }

  // Void ONE topping off a line, keeping the parent item. toppingVoids is a flat
  // list of { uuid (parent line), toppingId, name, parentName, qty }. Builds the
  // nested json2 the VOID_TOPPING reducer expects, and ships descriptor __items
  // (one "parent (topping)" row per void) so the existing __items-driven surfaces
  // — kitchen void slip, void dashboards, Activity — render the topping by name.
  const voidToppings = (
    reason: string,
    toppingVoids: Array<{ uuid: string; toppingId: string | number; name?: string; parentName?: string; qty: number; price?: number }>,
    opts: { printToKitchen?: boolean; copyToCashier?: boolean } = {},
  ) => {
    if (isVoiding.value) return
    if (!toppingVoids || toppingVoids.length === 0) return
    isVoiding.value = true
    try {
      const json2 = toppingVoids.reduce((acc: any, v) => {
        const qty = Number(v.qty) || 0
        if (qty <= 0) return acc
        acc[v.uuid] = acc[v.uuid] || {}
        // Negative-by-convention (reducer abs's it), same as voidItems.
        acc[v.uuid][String(v.toppingId)] = qty * -1
        return acc
      }, {})

      if (Object.keys(json2).length === 0) return

      const __items = toppingVoids
        .filter((v) => (Number(v.qty) || 0) > 0)
        .map((v) => ({
          name: v.parentName ? `${v.parentName} (${v.name || ''})` : (v.name || ''),
          quantity: Number(v.qty) || 0,
          price: Number(v.price) || 0,
          subItemId: v.toppingId,
        }))

      saveToJobchain(JobAction.VOID_TOPPING, json2, {
        __reason: reason || "",
        __items,
        ...(opts.printToKitchen === false ? { __noPrint: true } : {}),
        ...(opts.copyToCashier === true ? { __copyToCashier: true } : {}),
      })
    } finally {
      isVoiding.value = false
    }
  }



  // json2 per line is [mode, value]. For mode 1 (baht) `value` is the PER-PIECE
  // discount — never a pre-multiplied line total. The ×quantity happens when the
  // bill is built (discountItemLineFn), which is what keeps void/split/move right.
  const discountLines = (mode: number, value: number) => {
    const json2 = Object.keys(itemsSelected.value).reduce((acc: any, uuid: string) => {
      acc[uuid] = [mode, value]
      return acc
    }, {})

    saveToJobchain(JobAction.SET_DISCOUNT_ITEMS, json2, {
      discountRequest: {
        mode: mode,
        value: value,
      }
    })

  }

  const setDiscountOneItem = (item: any, mode: number, value: number) => {
    const json2 = {
      [item.uuid]: [mode, value]
    }

    saveToJobchain(JobAction.SET_DISCOUNT_ITEMS, json2, {
      discountRequest: {
        mode: mode,
        value: value,
      }
    })
  }

  const setPrice = (item, price: number) => {
    const json2 = {
      [item.uuid]: parseFloat(price)
    }

    saveToJobchain(JobAction.SET_PRICE, json2, {
      __items: [item]
    })
  }



  const splitItem = (item: any, qty: number) => {
    const json2 = {
      [item.uuid]: qty
    }
    saveToJobchain(JobAction.SPLIT, json2, {
      __items: [item],
    })
  }

  const setItemComment = (item, text) => {
    const json2 = {
      [item.uuid]: text
    }

    saveToJobchain(JobAction.SET_COMMENTS, json2, {
      __items: [item]
    })
  }

  const setDeliveryInfo = (deliveryInfo: any) => {
    saveToJobchain(JobAction.SET_DELIVERY_INFO, {}, {
      deliveryInfo,
      __items: []
    })
  }

  const saveToJobchain = (actionType: JobAction, json2: any, options?: any) => {

    const employeeStore = usePosEmployeesStore()

    const mode = data.value?.mode || JobMode.TABLE

    const _job = new JobBuilder()
      .withEmployee(employeeStore.getCurrentEmployee)
      .withTableAlias(selectedTableId.value)
      .withTableUuid(selectedTableId.value)
      .withActionType(actionType)
      .withMode(mode)
      .withJson2(json2)
      .withJobIdAndTimestamp(useServerTime().nowISO())

    // Only togo/job/delivery carry a queue label so VOID / discount / comment / etc.
    // log + print with the human nickName ("Queue #N") instead of the raw hash alias
    // (same as ADD/PAY). Plain tables identify by alias (T5) — skip nickName entirely
    // so we don't ship a redundant field on every table action job.
    if (mode !== JobMode.TABLE && data.value?.nickName) {
      _job.withNickName(data.value.nickName)
    }

    if (options) {
      _job.withCustom(options)
    }


    const itemsSorted = itemsDisplay.value.filter((item: any) => itemsSelected.value[item.uuid])
    if (itemsSorted.length > 0) {
      _job.withCustom({
        __items: itemsSorted
      })
    }

    const payload = _job.build()
    console.log(payload)

    const jobStore = usePosJobsStore()
    jobStore.add(payload, true)
    return payload
  }




  return {
    selectedTableId,
    clearTableSelected,
    itemsSelected,
    uuidQtyToVoid,
    forceShowSplitBillColumn,

    data,
    table,
    filteredBilling,
    isLock,
    selectItem,
    selectOnlyItem,
    selectAllItems,
    clearItemsSelected,
    setTable,
    setGuests,

    // getter
    getCountItemSelected,
    itemCount,
    itemsDisplay,
    summaryBillingMoney,
    billList,
    billFilter,
    selectedSplitBillNo,

    // actions
    addRequest,
    printPreview,
    move,
    voidItems,
    voidToppings,
    discountLines,
    setDiscountOneItem,
    setPrice,
    splitItem,
    saveToJobchain,
    setItemComment,
    setDeliveryInfo

  }

});
